import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { AlertCircle, Edit, Plus, Search, Trash2, X } from "lucide-react";
import LoadingScreen from "../../screens/LoadingScreen";
import { brokerageService } from "../../service/brokerageService";
import { AdminBrokerage } from "../../types/brokerages";
import { Button } from "../../components/misc/Button";
import { Input } from "../../components/misc/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/misc/Table";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../components/misc/Dialog";
import CustomToast from "../../components/misc/CustomToast";
import ConfirmPopup from "../../components/misc/ConfirmPopup";
import ColorField from "../../components/misc/ColorField";
import Spinner from "../../components/misc/Spinner";
import { validateLogoFile } from "../../utils/imageValidation";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import SimpleTooltip from "../../components/misc/SimpleTooltip";
import { Switch } from "../../components/misc/Switch";

const MAX_LOGOS = 5;
const MAX_NAME_LENGTH = 30;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const COLOR_LABELS = ["First Color", "Second Color", "Third Color"];
const DEFAULT_CREATE_COLORS = ["#193456", "#193456", "#193456"];
const MIME_TO_EXT: Record<string, string[]> = {
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/webp": ["webp"],
};

type CreatePhase = "idle" | "creating" | "uploading" | "partial-error";

interface StagedLogo {
  id: string;
  file: File;
  previewUrl: string;
  status: "pending" | "uploading" | "done" | "error";
  uploadedKey?: string;
}

const sortBrokerages = (list: AdminBrokerage[]): AdminBrokerage[] =>
  [...list].sort((a, b) => {
    const aIsOther = a.name.toLowerCase() === "other";
    const bIsOther = b.name.toLowerCase() === "other";
    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });

const Brokerages: React.FC = () => {
  const [brokerages, setBrokerages] = useState<AdminBrokerage[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Edit dialog
  const [selected, setSelected] = useState<AdminBrokerage | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [colorsDraft, setColorsDraft] = useState<string[]>([]);
  const [savingColors, setSavingColors] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Logo management (edit dialog)
  const [uploading, setUploading] = useState(false);
  const [logoToRemove, setLogoToRemove] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create dialog state machine
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createColors, setCreateColors] = useState<string[]>([
    ...DEFAULT_CREATE_COLORS,
  ]);
  const [stagedLogos, setStagedLogos] = useState<StagedLogo[]>([]);
  const [createPhase, setCreatePhase] = useState<CreatePhase>("idle");
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const createFileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirm
  const [brokerageToDelete, setBrokerageToDelete] =
    useState<AdminBrokerage | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Enable / disable toggle
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [brokerageToToggle, setBrokerageToToggle] =
    useState<AdminBrokerage | null>(null);

  const isMobile = useIsMobile(700);

  // Keep a ref in sync so the unmount cleanup can revoke all preview URLs
  const stagedLogosRef = useRef<StagedLogo[]>([]);
  useEffect(() => { stagedLogosRef.current = stagedLogos; }, [stagedLogos]);
  useEffect(() => () => { stagedLogosRef.current.forEach((l) => URL.revokeObjectURL(l.previewUrl)); }, []);

  // ── Derived create-dialog state ────────────────────────────────────────────
  const uploadedCount = stagedLogos.filter((l) => l.status === "done").length;
  const failedCount = stagedLogos.filter((l) => l.status === "error").length;
  const isCreateBusy =
    createPhase === "creating" || createPhase === "uploading";
  const isDirtyCreate =
    createName.trim().length > 0 ||
    stagedLogos.length > 0 ||
    createColors.some((c, i) => c !== DEFAULT_CREATE_COLORS[i]);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchBrokerages = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await brokerageService.getBrokerages();
      setBrokerages(data);
    } catch {
      toast.error(
        <CustomToast message="Failed to fetch brokerages" type="error" />,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchBrokerages();
  }, [fetchBrokerages]);

  const filtered = useMemo(
    () =>
      (brokerages ?? []).filter(
        (b) =>
          !b.isDeleted &&
          b.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [brokerages, searchTerm],
  );

  const applyUpdated = useCallback((updated: AdminBrokerage): void => {
    setBrokerages((prev) =>
      sortBrokerages(
        (prev ?? []).map((b) => (b._id === updated._id ? updated : b)),
      ),
    );
    setSelected((prev) => (prev && prev._id === updated._id ? updated : prev));
  }, []);

  // ── Edit dialog ────────────────────────────────────────────────────────────
  const openManage = (brokerage: AdminBrokerage): void => {
    setSelected(brokerage);
    const colors = [...brokerage.zapcapColors];
    while (colors.length < 3) colors.push("#193456");
    setColorsDraft(colors.slice(0, 3));
    setNameDraft(brokerage.name);
    setManageOpen(true);
  };

  const closeManage = (): void => {
    setManageOpen(false);
    setSelected(null);
    setColorsDraft([]);
    setNameDraft("");
    setLogoToRemove(null);
  };

  const isOther = (b: AdminBrokerage | null): boolean =>
    b?.name.toLowerCase() === "other";

  const handleSaveName = async (): Promise<void> => {
    if (!selected) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast.error(<CustomToast message="Name cannot be empty" type="error" />);
      return;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      toast.error(
        <CustomToast
          message={`Name must be ${MAX_NAME_LENGTH} characters or fewer`}
          type="error"
        />,
      );
      return;
    }
    if (trimmed.toLowerCase() === "other") {
      toast.error(
        <CustomToast
          message='A brokerage named "Other" already exists'
          type="error"
        />,
      );
      return;
    }
    setSavingName(true);
    try {
      const updated = await brokerageService.updateBrokerageName(
        selected._id,
        trimmed,
      );
      applyUpdated(updated);
      toast.success(<CustomToast message="Name updated" type="success" />);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to update name";
      toast.error(<CustomToast message={msg} type="error" />);
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveColors = async (): Promise<void> => {
    if (!selected) return;
    const invalid = colorsDraft.find((c) => !HEX_RE.test(c.trim()));
    if (invalid !== undefined) {
      toast.error(
        <CustomToast
          message={`Invalid hex colour: ${invalid || "(empty)"}`}
          type="error"
        />,
      );
      return;
    }
    setSavingColors(true);
    try {
      const updated = await brokerageService.updateColors(
        selected._id,
        colorsDraft.map((c) => c.trim()),
      );
      applyUpdated(updated);
      toast.success(<CustomToast message="Colors updated" type="success" />);
    } catch {
      toast.error(
        <CustomToast message="Failed to update colors" type="error" />,
      );
    } finally {
      setSavingColors(false);
    }
  };

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!selected || !file) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    const allowedExts = MIME_TO_EXT[file.type];
    if (!allowedExts) {
      toast.error(
        <CustomToast
          message="Unsupported format. Use PNG, JPEG, or WebP."
          type="error"
        />,
      );
      return;
    }
    if (!ext || !allowedExts.includes(ext)) {
      toast.error(
        <CustomToast
          message="File extension does not match image type"
          type="error"
        />,
      );
      return;
    }
    if (file.size === 0) {
      toast.error(<CustomToast message="File is empty" type="error" />);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(<CustomToast message="Image exceeds 5 MB" type="error" />);
      return;
    }
    const validation = await validateLogoFile(file);
    if (!validation.ok) {
      toast.error(
        <CustomToast message={validation.reason ?? "Invalid logo"} type="error" />,
      );
      return;
    }

    setUploading(true);
    try {
      const { signedUrl, key } = await brokerageService.getLogoUploadUrl(
        selected._id,
        {
          contentType: file.type,
          extension: ext,
          fileSize: file.size,
        },
      );
      await brokerageService.uploadToSignedUrl(signedUrl, file, file.type);
      const updated = await brokerageService.confirmLogo(selected._id, key);
      applyUpdated(updated);
      toast.success(<CustomToast message="Logo added" type="success" />);
    } catch {
      toast.error(<CustomToast message="Failed to upload logo" type="error" />);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async (): Promise<void> => {
    if (!selected || !logoToRemove) return;
    const logoId = logoToRemove;
    setRemoving(true);
    try {
      const updated = await brokerageService.removeLogo(selected._id, logoId);
      applyUpdated(updated);
      setLogoToRemove(null);
      toast.success(<CustomToast message="Logo removed" type="success" />);
    } catch {
      toast.error(<CustomToast message="Failed to remove logo" type="error" />);
    } finally {
      setRemoving(false);
    }
  };

  // ── Create dialog helpers ──────────────────────────────────────────────────
  const resetCreateDialog = useCallback((): void => {
    setCreateName("");
    setCreateColors([...DEFAULT_CREATE_COLORS]);
    setStagedLogos((prev) => {
      prev.forEach((l) => URL.revokeObjectURL(l.previewUrl));
      return [];
    });
    setCreatePhase("idle");
    setCreatedId(null);
    setShowDiscardConfirm(false);
  }, []);

  const tryCloseCreate = useCallback((): void => {
    if (isCreateBusy) return;
    if (createPhase === "partial-error") {
      resetCreateDialog();
      setCreateOpen(false);
      return;
    }
    if (isDirtyCreate) {
      setShowDiscardConfirm(true);
      return;
    }
    resetCreateDialog();
    setCreateOpen(false);
  }, [isCreateBusy, createPhase, isDirtyCreate, resetCreateDialog]);

  const removeStagedLogo = (id: string): void => {
    setStagedLogos((prev) => {
      const logo = prev.find((l) => l.id === id);
      if (logo) URL.revokeObjectURL(logo.previewUrl);
      return prev.filter((l) => l.id !== id);
    });
  };

  const handleStageFiles = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;

    const remainingSlots = MAX_LOGOS - stagedLogos.length;
    if (remainingSlots <= 0) {
      toast.error(
        <CustomToast
          message={`Maximum of ${MAX_LOGOS} logos reached`}
          type="error"
        />,
      );
      return;
    }

    const toProcess = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.error(
        <CustomToast
          message={`Only ${remainingSlots} slot${remainingSlots !== 1 ? "s" : ""} remaining — extra files were skipped.`}
          type="error"
        />,
      );
    }

    const valid: StagedLogo[] = [];
    for (const file of toProcess) {
      const ext = file.name.split(".").pop()?.toLowerCase();
      const allowedExts = MIME_TO_EXT[file.type];
      if (!allowedExts) {
        toast.error(
          <CustomToast
            message={`${file.name}: Unsupported format. Use PNG, JPEG, or WebP.`}
            type="error"
          />,
        );
        continue;
      }
      if (!ext || !allowedExts.includes(ext)) {
        toast.error(
          <CustomToast
            message={`${file.name}: Extension does not match image type`}
            type="error"
          />,
        );
        continue;
      }
      if (file.size === 0) {
        toast.error(
          <CustomToast message={`${file.name}: File is empty`} type="error" />,
        );
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          <CustomToast message={`${file.name}: Exceeds 5 MB`} type="error" />,
        );
        continue;
      }
      const validation = await validateLogoFile(file);
      if (!validation.ok) {
        toast.error(
          <CustomToast
            message={`${file.name}: ${validation.reason}`}
            type="error"
          />,
        );
        continue;
      }
      valid.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        status: "pending",
      });
    }

    if (valid.length > 0) {
      setStagedLogos((prev) => [...prev, ...valid]);
    }
  };

  // Upload a given list of logos against an existing brokerage ID.
  // Updates per-logo status in state as each upload progresses.
  const uploadLogos = async (
    brokerageId: string,
    logos: StagedLogo[],
  ): Promise<{ anyFailed: boolean; latest: AdminBrokerage | null }> => {
    let anyFailed = false;
    let latest: AdminBrokerage | null = null;

    for (const staged of logos) {
      setStagedLogos((prev) =>
        prev.map((l) =>
          l.id === staged.id ? { ...l, status: "uploading" } : l,
        ),
      );
      try {
        const ext = staged.file.name.split(".").pop()?.toLowerCase() ?? "";
        let key: string;

        if (staged.uploadedKey) {
          // PUT already succeeded on a prior attempt — skip re-upload and just retry confirm
          key = staged.uploadedKey;
        } else {
          const { signedUrl, key: newKey } = await brokerageService.getLogoUploadUrl(
            brokerageId,
            { contentType: staged.file.type, extension: ext, fileSize: staged.file.size },
          );
          key = newKey;
          await brokerageService.uploadToSignedUrl(signedUrl, staged.file, staged.file.type);
          // Record the key so a retry won't re-upload the same file
          setStagedLogos((prev) =>
            prev.map((l) => (l.id === staged.id ? { ...l, uploadedKey: key } : l)),
          );
        }

        latest = await brokerageService.confirmLogo(brokerageId, key);
        setStagedLogos((prev) =>
          prev.map((l) => (l.id === staged.id ? { ...l, status: "done" } : l)),
        );
      } catch {
        anyFailed = true;
        setStagedLogos((prev) =>
          prev.map((l) => (l.id === staged.id ? { ...l, status: "error" } : l)),
        );
      }
    }

    return { anyFailed, latest };
  };

  // ── Create brokerage (full flow) ───────────────────────────────────────────
  const handleCreate = async (): Promise<void> => {
    const trimmed = createName.trim();
    if (!trimmed) {
      toast.error(<CustomToast message="Name cannot be empty" type="error" />);
      return;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      toast.error(
        <CustomToast
          message={`Name must be ${MAX_NAME_LENGTH} characters or fewer`}
          type="error"
        />,
      );
      return;
    }
    if (trimmed.toLowerCase() === "other") {
      toast.error(
        <CustomToast
          message='A brokerage named "Other" already exists'
          type="error"
        />,
      );
      return;
    }
    const invalidColor = createColors.find((c) => !HEX_RE.test(c.trim()));
    if (invalidColor !== undefined) {
      toast.error(
        <CustomToast
          message={`Invalid hex colour: ${invalidColor || "(empty)"}`}
          type="error"
        />,
      );
      return;
    }

    // Step 1: create brokerage
    setCreatePhase("creating");
    let created: AdminBrokerage;
    try {
      created = await brokerageService.createBrokerage(trimmed);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to create brokerage";
      toast.error(<CustomToast message={msg} type="error" />);
      setCreatePhase("idle");
      return;
    }

    setCreatedId(created._id);

    // Step 1b: apply colors if changed from defaults (non-blocking — brokerage already created)
    const colorsChanged = createColors.some(
      (c, i) => c.trim() !== DEFAULT_CREATE_COLORS[i],
    );
    if (colorsChanged) {
      try {
        created = await brokerageService.updateColors(
          created._id,
          createColors.map((c) => c.trim()),
        );
      } catch {
        toast.error(
          <CustomToast
            message="Brokerage created but colors could not be applied. Update them from Edit."
            type="error"
          />,
        );
      }
    }

    // Add to table immediately so it appears even if logo uploads fail
    setBrokerages((prev) => sortBrokerages([...(prev ?? []), created]));

    // Step 2: upload staged logos (if any)
    if (stagedLogos.length === 0) {
      toast.success(<CustomToast message="Brokerage created" type="success" />);
      resetCreateDialog();
      setCreateOpen(false);
      return;
    }

    setCreatePhase("uploading");
    const { anyFailed, latest } = await uploadLogos(created._id, stagedLogos);
    if (latest) applyUpdated(latest);

    if (!anyFailed) {
      toast.success(<CustomToast message="Brokerage created" type="success" />);
      resetCreateDialog();
      setCreateOpen(false);
    } else {
      setCreatePhase("partial-error");
    }
  };

  // Retry only the logos that failed — brokerage already exists (createdId set)
  const handleRetryFailed = async (): Promise<void> => {
    if (!createdId) return;
    const toRetry = stagedLogos.filter((l) => l.status === "error");
    setCreatePhase("uploading");

    const { anyFailed, latest } = await uploadLogos(createdId, toRetry);
    if (latest) applyUpdated(latest);

    if (!anyFailed) {
      toast.success(
        <CustomToast message="All logos uploaded" type="success" />,
      );
      resetCreateDialog();
      setCreateOpen(false);
    } else {
      setCreatePhase("partial-error");
    }
  };

  // ── Enable / disable brokerage ─────────────────────────────────────────────
  const handleToggleEnabled = async (
    brokerage: AdminBrokerage,
  ): Promise<void> => {
    setTogglingId(brokerage._id);
    try {
      const updated = await brokerageService.toggleStatus(
        brokerage._id,
        !brokerage.isEnabled,
      );
      applyUpdated(updated);
      toast.success(
        <CustomToast
          message={
            updated.isEnabled ? "Brokerage enabled" : "Brokerage disabled"
          }
          type="success"
        />,
      );
    } catch {
      toast.error(
        <CustomToast message="Failed to update status" type="error" />,
      );
    } finally {
      setTogglingId(null);
    }
  };

  // ── Delete brokerage (soft) ────────────────────────────────────────────────
  const handleDelete = async (): Promise<void> => {
    if (!brokerageToDelete) return;
    const target = brokerageToDelete;
    setDeleting(true);
    try {
      await brokerageService.deleteBrokerage(target._id);
      // Soft delete — mark in local state instead of removing the row
      setBrokerages((prev) =>
        sortBrokerages(
          (prev ?? []).map((b) =>
            b._id === target._id ? { ...b, isDeleted: true } : b,
          ),
        ),
      );
      setBrokerageToDelete(null);
      toast.success(<CustomToast message="Brokerage deleted" type="success" />);
    } catch {
      toast.error(
        <CustomToast message="Failed to delete brokerage" type="error" />,
      );
    } finally {
      setDeleting(false);
    }
  };

  const atLogoLimit = (selected?.logos.length ?? 0) >= MAX_LOGOS;
  const editIsBlocked = uploading || savingColors || savingName;

  return (
    <div className="flex flex-col h-full p-4 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-primary">
          Broker Branding
        </h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Add brokerage
        </Button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search brokerages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>

        {loading ? (
          <LoadingScreen />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Colors</TableHead>
                <TableHead>Logos</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((brokerage) => (
                <TableRow key={brokerage._id}>
                  <TableCell className="font-medium">
                    {brokerage.name}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {brokerage.zapcapColors.map((color, i) => (
                        <span
                          key={`${color}-${i}`}
                          className="h-5 w-5 rounded-full border border-gray-200"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{brokerage.logoCount}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-3">
                      <Switch
                        checked={brokerage.isEnabled}
                        checkedLabel="Active"
                        uncheckedLabel="Inactive"
                        disabled={
                          isOther(brokerage) || togglingId === brokerage._id
                        }
                        onCheckedChange={() => setBrokerageToToggle(brokerage)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openManage(brokerage)}
                      >
                        <Edit className="mr-2 h-4 w-4" /> Edit
                      </Button>
                      <SimpleTooltip
                        message="This brokerage is the default for users with no brokerage linked and cannot be deleted"
                        visible={isOther(brokerage)}
                        showArrow
                      >
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isOther(brokerage)}
                          onClick={() => setBrokerageToDelete(brokerage)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </SimpleTooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-muted-foreground py-6"
                  >
                    No brokerages found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ── Create dialog ── */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) tryCloseCreate();
        }}
      >
        <DialogContent className="bg-white p-0 relative max-w-lg">
          {/* Header */}
          <div className="relative border-b border-gray-100 px-6 py-5">
            <DialogTitle className="text-center text-lg font-semibold">
              Add brokerage
            </DialogTitle>
            <button
              type="button"
              onClick={tryCloseCreate}
              disabled={isCreateBusy}
              aria-label="Close"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="relative px-6 py-6 space-y-6 max-h-[65vh] overflow-y-auto">
            {/* Creating overlay */}
            {createPhase === "creating" && (
              <div className="absolute inset-0 bg-white/90 flex items-center justify-center z-20">
                <Spinner loadingMessage="Creating brokerage…" />
              </div>
            )}

            {/* Name */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">
                Name
              </label>
              <Input
                placeholder="e.g. Century 21"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                disabled={createPhase !== "idle"}
                maxLength={MAX_NAME_LENGTH}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && createPhase === "idle") {
                    void handleCreate();
                  }
                }}
              />
              <p
                className={cn(
                  "text-xs text-right",
                  createName.trim().length > MAX_NAME_LENGTH
                    ? "text-destructive"
                    : "text-muted-foreground",
                )}
              >
                {createName.trim().length}/{MAX_NAME_LENGTH}
              </p>
            </div>

            <div className="border-t border-gray-100" />

            {/* Branding colors */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700">
                Branding colors
              </label>
              <div
                className={cn(
                  "grid gap-3",
                  isMobile ? "grid-cols-2" : "grid-cols-3",
                )}
              >
                {COLOR_LABELS.map((label, index) => (
                  <ColorField
                    key={label}
                    label={label}
                    color={createColors[index] ?? "#000000"}
                    disabled={createPhase !== "idle"}
                    onChange={(c) =>
                      setCreateColors((prev) =>
                        prev.map((existing, i) => (i === index ? c : existing)),
                      )
                    }
                  />
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100" />

            {/* Logos */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm font-semibold text-gray-700">
                  Logos ({stagedLogos.length}/{MAX_LOGOS}){" "}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </label>
                {createPhase === "idle" && stagedLogos.length < MAX_LOGOS && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => createFileInputRef.current?.click()}
                  >
                    <Plus className="mr-1 h-4 w-4" /> Add logo
                  </Button>
                )}
                <input
                  ref={createFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => void handleStageFiles(e)}
                />
              </div>

              {/* Upload progress bar */}
              {createPhase === "uploading" && stagedLogos.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Uploading logos…</span>
                    <span>
                      {uploadedCount}/{stagedLogos.length}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{
                        width: `${(uploadedCount / stagedLogos.length) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Partial error banner */}
              {createPhase === "partial-error" && (
                <div className="rounded-md bg-[#fef2f2] border border-[#fecaca] px-3 py-2.5 text-sm text-[#b91c1c]">
                  Brokerage created, but {failedCount} logo
                  {failedCount !== 1 ? "s" : ""} failed to upload. Retry below
                  or close and add logos later from Edit.
                </div>
              )}

              {/* Logo grid */}
              {stagedLogos.length > 0 ? (
                <div
                  className={cn(
                    "grid gap-3",
                    isMobile ? "grid-cols-2" : "grid-cols-4",
                  )}
                >
                  {stagedLogos.map((logo) => (
                    <div
                      key={logo.id}
                      className="relative aspect-square rounded-lg border border-gray-200 bg-gray-100 overflow-hidden"
                    >
                      <img
                        src={logo.previewUrl}
                        alt="Logo preview"
                        className="h-full w-full object-contain"
                      />
                      {/* Status overlay */}
                      {logo.status === "error" && (
                        <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                          <AlertCircle className="h-6 w-6 text-red-600" />
                        </div>
                      )}
                      {/* Remove only available in idle */}
                      {createPhase === "idle" && (
                        <button
                          type="button"
                          onClick={() => removeStagedLogo(logo.id)}
                          className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1.5 shadow-sm border border-gray-200 text-[#d50000] hover:bg-[#d50000] hover:text-white transition-colors"
                          aria-label="Remove logo"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                createPhase === "idle" && (
                  <p className="text-sm text-muted-foreground">
                    No logos added. You can add them later from Edit.
                  </p>
                )
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3">
            {createPhase === "idle" && (
              <>
                <Button variant="outline" size="sm" onClick={tryCloseCreate}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => void handleCreate()}
                  disabled={
                    !createName.trim() ||
                    createName.trim().length > MAX_NAME_LENGTH
                  }
                >
                  Create
                </Button>
              </>
            )}
            {createPhase === "uploading" && (
              <p className="text-sm text-muted-foreground">
                Please wait while logos are uploading…
              </p>
            )}
            {createPhase === "partial-error" && (
              <>
                <Button variant="outline" size="sm" onClick={tryCloseCreate}>
                  Close anyway
                </Button>
                <Button size="sm" onClick={() => void handleRetryFailed()}>
                  Retry failed ({failedCount})
                </Button>
              </>
            )}
          </div>

          {/* Discard confirm — shown over the dialog when user tries to close with dirty state */}
          <ConfirmPopup
            open={showDiscardConfirm}
            title="Discard changes?"
            message="You have unsaved changes. Close without creating the brokerage?"
            confirmText="Discard"
            cancelText="Keep editing"
            confirmVariant="destructive"
            onConfirm={() => {
              setShowDiscardConfirm(false);
              resetCreateDialog();
              setCreateOpen(false);
            }}
            onCancel={() => setShowDiscardConfirm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* ── Edit dialog ── */}
      <Dialog
        open={manageOpen}
        onOpenChange={(open) => {
          if (!open && !editIsBlocked) closeManage();
        }}
      >
        <DialogContent className="bg-white p-0 relative max-w-2xl">
          <div className="relative border-b border-gray-100 px-5 py-4 min-[640px]:px-6">
            <DialogTitle className="text-center text-lg min-[640px]:text-xl font-semibold">
              {selected ? `Edit ${selected.name}` : "Edit"}
            </DialogTitle>
            <button
              type="button"
              onClick={() => {
                if (!editIsBlocked) closeManage();
              }}
              disabled={editIsBlocked}
              aria-label="Close"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {selected && (
            <div className="px-5 py-5 min-[640px]:px-6 space-y-6">
              {/* Name */}
              <section className="space-y-3">
                <h3 className="text-base font-semibold text-primary">Name</h3>
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Input
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      disabled={savingName || isOther(selected)}
                      className="flex-1"
                      maxLength={MAX_NAME_LENGTH}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void handleSaveName();
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={() => void handleSaveName()}
                      disabled={savingName || isOther(selected)}
                    >
                      {savingName ? "Saving..." : "Save"}
                    </Button>
                  </div>
                  {!isOther(selected) && (
                    <p
                      className={cn(
                        "text-xs text-right",
                        nameDraft.trim().length > MAX_NAME_LENGTH
                          ? "text-destructive"
                          : "text-muted-foreground",
                      )}
                    >
                      {nameDraft.trim().length}/{MAX_NAME_LENGTH}
                    </p>
                  )}
                </div>
              </section>

              <div className="border-t border-gray-100" />

              {/* Colors */}
              <section className="space-y-3">
                <h3 className="text-base font-semibold text-primary">
                  Branding colors
                </h3>
                <div
                  className={cn(
                    "grid gap-2",
                    isMobile ? "grid-cols-2" : "grid-cols-3",
                  )}
                >
                  {COLOR_LABELS.map((label, index) => (
                    <ColorField
                      key={label}
                      label={label}
                      color={colorsDraft[index] ?? "#000000"}
                      disabled={savingColors}
                      onChange={(c) =>
                        setColorsDraft((prev) =>
                          prev.map((existing, i) =>
                            i === index ? c : existing,
                          ),
                        )
                      }
                    />
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => void handleSaveColors()}
                    disabled={savingColors}
                  >
                    {savingColors ? "Saving..." : "Save colors"}
                  </Button>
                </div>
              </section>

              {/* Logos — hidden for "other" */}
              {!isOther(selected) && (
                <>
                  <div className="border-t border-gray-100" />
                  <section className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-base font-semibold text-primary">
                        Logos ({selected.logos.length}/{MAX_LOGOS})
                      </h3>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={atLogoLimit || uploading}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        {uploading ? "Uploading..." : "Add logo"}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => void handleFileSelect(e)}
                      />
                    </div>
                    {atLogoLimit && (
                      <p className="text-xs text-muted-foreground">
                        Maximum of {MAX_LOGOS} logos reached. Remove one to add
                        another.
                      </p>
                    )}
                    <div
                      className={cn(
                        "grid gap-3",
                        isMobile ? "grid-cols-2" : "grid-cols-5",
                      )}
                    >
                      {selected.logos.map((logo) => (
                        <div
                          key={logo.id}
                          className="group relative aspect-square rounded-lg border border-gray-200 bg-gray-100 overflow-hidden"
                        >
                          <img
                            src={logo.signedUrl}
                            alt="Brokerage logo"
                            className="h-full w-full object-contain"
                          />
                          <button
                            type="button"
                            disabled={uploading}
                            onClick={() => setLogoToRemove(logo.id)}
                            className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1.5 shadow-sm border border-gray-200 text-[#d50000] hover:bg-[#d50000] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Remove logo"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                      {selected.logos.length === 0 && (
                        <p className="col-span-full text-sm text-muted-foreground py-4 text-center">
                          No logos yet. Click &quot;Add logo&quot; to upload
                          one.
                        </p>
                      )}
                    </div>
                  </section>
                </>
              )}
            </div>
          )}

          <ConfirmPopup
            open={logoToRemove !== null}
            title="Remove logo"
            message="Are you sure you want to remove this logo? It can be re-added later."
            confirmText={removing ? "Removing..." : "Remove"}
            cancelText="Cancel"
            confirmVariant="destructive"
            cancelDisabled={removing}
            onConfirm={() => void handleRemoveLogo()}
            onCancel={() => {
              if (!removing) setLogoToRemove(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* ── Toggle active/inactive confirm ── */}
      <ConfirmPopup
        open={brokerageToToggle !== null}
        title={
          brokerageToToggle?.isEnabled
            ? "Deactivate brokerage"
            : "Activate brokerage"
        }
        message={
          brokerageToToggle?.isEnabled
            ? `Are you sure you want to mark "${brokerageToToggle?.name}" as inactive?`
            : `Are you sure you want to mark "${brokerageToToggle?.name}" as active?`
        }
        confirmText={brokerageToToggle?.isEnabled ? "Deactivate" : "Activate"}
        cancelText="Cancel"
        confirmVariant={
          brokerageToToggle?.isEnabled ? "destructive" : "default"
        }
        onConfirm={() => {
          if (brokerageToToggle) {
            void handleToggleEnabled(brokerageToToggle);
          }
          setBrokerageToToggle(null);
        }}
        onCancel={() => setBrokerageToToggle(null)}
      />

      {/* ── Delete confirm ── */}
      <ConfirmPopup
        open={brokerageToDelete !== null}
        title="Delete brokerage"
        message={`Are you sure you want to delete "${brokerageToDelete?.name}"?`}
        confirmText={deleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmVariant="destructive"
        cancelDisabled={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => {
          if (!deleting) setBrokerageToDelete(null);
        }}
      />
    </div>
  );
};

export default Brokerages;
